import React from 'react';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import css from './SectionNewsletter.module.css';

/**
 * Newsletter sign-up section.
 *
 * This used to say "Thanks for subscribing" and then throw the address away: there
 * was no mailing list behind it, so every address entered since launch would have
 * been lost while the person was told they were on the list.
 *
 * There is no server endpoint in this app to collect addresses, and adding one is
 * outside the frontend. What an email provider (Mailchimp, Klaviyo, Brevo, …) does
 * give you is a form endpoint that accepts an ordinary browser form post, which is
 * exactly what their copy-paste "embed" code uses. So the section is driven by one
 * environment variable:
 *
 *   REACT_APP_NEWSLETTER_FORM_ACTION - the provider's form action URL
 *   REACT_APP_NEWSLETTER_EMAIL_FIELD - the field name it expects (defaults to EMAIL,
 *                                      which is what Mailchimp uses)
 *
 * With it set, the form posts straight to the provider and the provider confirms the
 * sign-up on its own page. With it unset the whole section is hidden, because a
 * sign-up box that does nothing is worse than no sign-up box.
 */

// Mailchimp's embedded forms name the field EMAIL; Klaviyo and Brevo use 'email'.
const DEFAULT_EMAIL_FIELD_NAME = 'EMAIL';

const SectionNewsletter = () => {
  const intl = useIntl();
  const formAction = process.env.REACT_APP_NEWSLETTER_FORM_ACTION;
  const emailFieldName = process.env.REACT_APP_NEWSLETTER_EMAIL_FIELD || DEFAULT_EMAIL_FIELD_NAME;

  if (!formAction) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.panel}>
          <h2 className={css.title}>
            <FormattedMessage id="LandingPage.newsletter.title" defaultMessage="Stay in the know" />
          </h2>
          <p className={css.subtitle}>
            <FormattedMessage
              id="LandingPage.newsletter.subtitle"
              defaultMessage="Get new brands, products and the evidence behind their credentials in your inbox."
            />
          </p>

          <form
            className={css.form}
            action={formAction}
            method="post"
            // The provider shows its own confirmation page; opening it in a new tab
            // means the shopper doesn't lose their place on the homepage.
            target="_blank"
            rel="noopener noreferrer"
          >
            <label className={css.srOnly} htmlFor="newsletter-email">
              <FormattedMessage id="LandingPage.newsletter.label" defaultMessage="Email address" />
            </label>
            <input
              id="newsletter-email"
              name={emailFieldName}
              type="email"
              required
              className={css.input}
              placeholder={intl.formatMessage({
                id: 'LandingPage.newsletter.placeholder',
                defaultMessage: 'Enter your email',
              })}
            />
            <button type="submit" className={css.button}>
              <FormattedMessage id="LandingPage.newsletter.button" defaultMessage="Subscribe" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SectionNewsletter;
